import {
  Button,
  ButtonProps,
  Checkbox,
  Flex,
  FormControl,
  Input,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
} from '@chakra-ui/react';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { TexasFormLabel } from '@texas/components/shared/form/TexasFormLabel';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Customer,
  CreateCustomerRequest,
  customersApi,
  CustomerType,
} from '@texas/api/endpoints/customersApi';
import { SubmitButton } from '@texas/components/shared/form/SubmitButton';
import { ErrorLabel } from '@texas/components/shared/ErrorLabel';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { Role } from '@texas/api/endpoints/userApi';
import { useActiveSession } from '@texas/hooks/useSession';
import { hasRole } from '@texas/utils/helpers/roleHelpers';
import { TexasSelect } from '@texas/components/shared/form/TexasSelect';
import { customerEvents } from '../events';

const customerTypes = [
  { label: 'Brand', value: CustomerType.Brand },
  { label: 'Vendor', value: CustomerType.Vendor },
];

interface NewCustomerProps {
  onCreated?: (customer: Customer) => void;
  submitLabel?: string;
  buttonLabel?: string;
  buttonProps?: ButtonProps;
}

export function NewCustomer({
  buttonLabel,
  buttonProps,
  onCreated,
  submitLabel,
}: NewCustomerProps = {}) {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const { t } = useTranslation();
  const toast = useToast();
  const { error, loading, request } = useApiRequest(
    customersApi.createCustomer,
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    reset,
  } = useForm<CreateCustomerRequest>({
    defaultValues: { customerType: CustomerType.Brand },
  });

  const customerTypeWatcher = useWatch({ control, name: 'customerType' });

  const onSubmit = async (data: CreateCustomerRequest) => {
    const response = await request(data);
    if (response.hasError) {
      toast({
        title: t('general.createFailed'),
        description: error?.message,
        status: 'error',
        isClosable: true,
      });
      return;
    }
    toast({
      title: t('general.created'),
      status: 'success',
      isClosable: true,
    });
    customerEvents.brandCreated.dispatch();
    onCreated?.(response.data);
    reset();
    onClose();
  };

  const session = useActiveSession();
  if (
    !hasRole(session.currentUser.role, [
      Role.InternalAdministrator,
      Role.SystemAdministrator,
    ])
  ) {
    return null;
  }

  return (
    <>
      <Button
        onClick={onOpen}
        {...(buttonProps
          ? buttonProps
          : {
              className: 'md-raised',
              height: '36px',
              fontSize: 14,
              padding: 1.5,
              variant: 'texas-solid',
            })}
      >
        {buttonLabel ?? t('general.newBrand')}
      </Button>

      <Modal onClose={onClose} isOpen={isOpen} closeOnOverlayClick={false}>
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalHeader fontWeight="bold">
            {customerTypeWatcher == CustomerType.Brand
              ? t('customer.createNewBrand')
              : t('customer.createNewVendor')}
          </ModalHeader>
          <ModalBody>
            {error && <ErrorDetails error={error} />}
            <form onSubmit={handleSubmit(onSubmit)}>
              <Flex direction="column" gap={4} pb={2}>
                <FormControl isRequired={true} isInvalid={!!errors.name}>
                  <TexasFormLabel>{t('general.name')}</TexasFormLabel>
                  <Input
                    variant="outline"
                    {...register('name', {
                      required: true,
                      maxLength: {
                        value: 100,
                        message: t('errors.maxLength', { count: 100 }),
                      },
                    })}
                    placeholder={t('general.name')}
                  />
                  <ErrorLabel text={errors.name?.message} />
                </FormControl>
                <FormControl>
                  <TexasFormLabel>{t('general.type')}</TexasFormLabel>
                  <Controller
                    name="customerType"
                    control={control}
                    render={({ field }) => (
                      <TexasSelect
                        {...field}
                        required={true}
                        onChange={(e) => {
                          field.onChange(e?.value ?? null);
                        }}
                        value={customerTypes.find(
                          (x) => x.value === field.value,
                        )}
                        options={customerTypes}
                      />
                    )}
                  />
                  <ErrorLabel text={errors.customerType?.message} />
                </FormControl>
                <FormControl>
                  <Controller
                    name="bcSampleApprovalRequired"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <Flex gap={2}>
                        <Checkbox onChange={onChange} isChecked={value} />
                        <TexasFormLabel>
                          {t('customer.bcSampleApprovalRequired')}
                        </TexasFormLabel>
                      </Flex>
                    )}
                  />
                </FormControl>
                <SubmitButton loading={loading}>
                  {submitLabel ?? t('general.save')}
                </SubmitButton>
              </Flex>
            </form>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}
